/**
 * apps/command/src/screens/AlertsScreen.tsx — §16 screen 8, and the half of
 * §4.4's lifecycle the feed deliberately does not offer.
 *
 * ============================================================================
 * RESOLVING LIVES HERE, NOT IN THE FEED, AND IT DEMANDS A NOTE.
 * ============================================================================
 *
 * §4.4's lifecycle ends "resolved", and a resolution without a note is an alert
 * nobody can learn from six months later — the queue's entire value in an
 * incident review is the notes. The API already refuses a note-less resolve
 * with 422; this is the screen that lets a human satisfy it.
 *
 * A ONE-CLICK RESOLVE IN A LIST IS HOW A QUEUE GETS EMPTIED WITHOUT ANYTHING
 * BEING FIXED. So `AlertFeed` offers acknowledge only, and closing an alert
 * costs a deliberate navigation and a sentence. That friction is the feature.
 *
 * ============================================================================
 * IT SAYS WHEN A RESOLVE WILL NOT STICK.
 * ============================================================================
 *
 * The engine re-evaluates every 60 s and re-opens any condition that still
 * holds. An operator may resolve something the rules still see — they may know
 * something the rules do not — and that is allowed. But being surprised by it
 * is not: if `last_seen_at` is recent, this screen says so BEFORE the button,
 * in a sentence, rather than letting the alert silently reappear in the feed
 * two minutes later looking like a bug.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { ageLabel, stamp } from '../lib/format.js';
import {
  Button,
  EmptyState,
  Field,
  Notice,
  Panel,
  PanelHeader,
  Skeleton,
  StatusPill,
  TextInput,
} from '../ui/index.jsx';
import { IconBack, IconChevron } from '../ui/icons.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';
import { BarRow } from '../ui/charts.jsx';

type MbtAlert = components['schemas']['MbtAlert'];

/**
 * How recently the engine must have seen a condition for a resolve to be
 * futile. One evaluation pass plus slack: anything inside this window is a
 * condition the next pass will almost certainly re-raise.
 */
const STILL_HOLDING_MS = 90_000;

function ruleLabel(rule: string): string {
  return hasKey(`alert.rule.${rule}`) ? t(`alert.rule.${rule}`) : rule;
}

export function AlertsScreen(): React.JSX.Element {
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('id');

  const [open, setOpen] = useState<MbtAlert[] | null>(null);
  const [resolved, setResolved] = useState<MbtAlert[] | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    try {
      const [openList, history] = await Promise.all([
        api().GET('/mbt/fleet/alerts'),
        api().GET('/mbt/fleet/alerts', { params: { query: { resolved: true, limit: 50 } } }),
      ]);
      setOpen(unwrap(openList).items as MbtAlert[]);
      setResolved(unwrap(history).items as MbtAlert[]);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('error.network'));
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 15_000);
    return () => clearInterval(timer);
  }, [load]);

  const selected = open?.find((alert) => alert.id === selectedId) ?? null;

  // Clearing the selection when the alert is gone — resolved here, or resolved
  // by the engine between polls. Leaving a dead id in the URL would render an
  // empty detail panel that looks like a fault.
  useEffect(() => {
    if (selectedId && open !== null && !selected) {
      setParams({}, { replace: true });
      setNote('');
    }
  }, [selectedId, open, selected, setParams]);

  async function resolve(alert: MbtAlert): Promise<void> {
    if (!note.trim()) {
      setError(t('alert.resolveNoteRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api().PATCH('/mbt/fleet/alerts/{alert_id}', {
        params: { path: { alert_id: alert.id } },
        body: { resolve: true, resolution_note: note.trim() },
      });
      setNote('');
      setParams({}, { replace: true });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 422) {
        setError(t('alert.resolveNoteRequired'));
      } else setError(t('error.network'));
    } finally {
      setBusy(false);
    }
  }

  async function acknowledge(alert: MbtAlert): Promise<void> {
    setBusy(true);
    try {
      await api().PATCH('/mbt/fleet/alerts/{alert_id}', {
        params: { path: { alert_id: alert.id } },
        body: { acknowledge: true },
      });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('error.network'));
    } finally {
      setBusy(false);
    }
  }

  if (selected) {
    const lastSeen = new Date(selected.last_seen_at);
    const stillHolding = now.getTime() - lastSeen.getTime() < STILL_HOLDING_MS;
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => {
            setParams({}, { replace: true });
            setNote('');
            setError(null);
          }}
          className="flex items-center gap-1.5 text-micro text-ink-500 transition-colors duration-state hover:text-ink-300"
        >
          <IconBack className="h-4 w-4" />
          {t('alert.back')}
        </button>

        <Panel>
          <PanelHeader
            title={t('alert.detail')}
            aside={<StatusPill status={selected.severity === 'critical' ? 'critical' : 'warning'} label={t(`status.${selected.severity}`)} />}
          />
          <div className="space-y-4 px-4 py-4">
            <div>
              <p className="text-lead font-medium text-ink-100">
                <Link
                  to={`/fleet/${selected.device_id}`}
                  className="tabular-nums underline decoration-hairline underline-offset-4 hover:text-brand"
                >
                  {selected.short_code}
                </Link>{' '}
                {ruleLabel(selected.rule)}
              </p>
              <p className="text-micro text-ink-500">{selected.mahalla_name}</p>
            </div>

            <dl className="divide-y divide-hairline text-label">
              <div className="flex justify-between py-1.5">
                <dt className="text-ink-500">{t('alert.opened')}</dt>
                <dd className="tabular-nums text-ink-100">
                  {stamp(new Date(selected.opened_at))} ·{' '}
                  {ageLabel(new Date(selected.opened_at), now, t)}
                </dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-ink-500">{t('alert.lastSeen')}</dt>
                <dd className="tabular-nums text-ink-100">{ageLabel(lastSeen, now, t)}</dd>
              </div>
              <div className="flex justify-between py-1.5">
                <dt className="text-ink-500">{t('alert.reading')}</dt>
                {/*
                  The reading that raised it, rendered as-is. Machine state
                  only, never citizen data (§4.4 / migration 0026), which is
                  what makes showing it verbatim safe — and it is what lets an
                  alert explain itself after the telemetry behind it has been
                  swept.
                */}
                <dd className="tabular-nums text-ink-300">
                  {Object.entries(selected.detail ?? {})
                    .map(([key, value]) => `${key}=${String(value)}`)
                    .join(' · ') || '—'}
                </dd>
              </div>
              {selected.acknowledged_by_name ? (
                <div className="flex justify-between py-1.5">
                  <dt className="text-ink-500">{t('alert.acknowledge')}</dt>
                  <dd className="text-ink-100">
                    {t('alert.acknowledged', { name: selected.acknowledged_by_name })}
                  </dd>
                </div>
              ) : null}
              {/* §4.8's chain. Shown as a FACT about who was told, not as a
                  warning — an operator reading this needs to know the duty
                  manager already has it, so they do not call them again. */}
              {selected.escalated_level && selected.escalated_level > 0 ? (
                <div className="flex justify-between py-1.5">
                  <dt className="text-ink-500">{t('alert.escalationNote')}</dt>
                  <dd className="text-warn">
                    {hasKey(`alert.escalated.${selected.escalated_level}`)
                      ? t(`alert.escalated.${selected.escalated_level}`)
                      : String(selected.escalated_level)}
                  </dd>
                </div>
              ) : null}
            </dl>

            {stillHolding ? (
              // Said BEFORE the button, not after the surprise.
              <Notice tone="warn">{t('alert.stillOpen')}</Notice>
            ) : null}

            <Field label={t('alert.resolveNote')} hint={t('alert.resolveNoteHint')}>
              <TextInput
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={500}
              />
            </Field>

            {error ? <Notice tone="crit">{error}</Notice> : null}

            <div className="flex gap-3">
              <Button onClick={() => void resolve(selected)} disabled={busy}>
                {t('alert.resolve')}
              </Button>
              {selected.acknowledged_at ? null : (
                <Button tone="quiet" onClick={() => void acknowledge(selected)} disabled={busy}>
                  {t('alert.acknowledge')}
                </Button>
              )}
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  /**
   * Which rules are firing, as a chart.
   *
   * A list of alerts answers «what is wrong»; this answers «what keeps going
   * wrong», which is the question that changes what somebody FIXES. Five
   * `disk_low` alerts across five kiosks is a procurement problem, not five
   * incidents, and a list one row per kiosk never says so.
   *
   * Built from the OPEN alerts plus the resolved history, because a rule that
   * fires and is closed all day is exactly the one a list hides.
   */
  const ruleBars = (() => {
    const counts = new Map<string, number>();
    for (const alert of [...(open ?? []), ...(resolved ?? [])]) {
      counts.set(alert.rule, (counts.get(alert.rule) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([rule, count]) => ({
        key: rule,
        label: hasKey(`alert.rule.${rule}`) ? t(`alert.rule.${rule}`) : rule,
        value: count,
        highlight: (open ?? []).some((alert) => alert.rule === rule),
      }));
  })();

  const criticalOpen = (open ?? []).filter((alert) => alert.severity === 'critical').length;
  const unacknowledged = (open ?? []).filter((alert) => !alert.acknowledged_at).length;
  const escalated = (open ?? []).filter((alert) => (alert.escalated_level ?? 0) > 0).length;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('alert.eyebrow')}</span>}
        title={t('nav.alerts')}
        subtitle={t('alert.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {open?.length ?? 0}
          </p>
        }
      />

      {error ? <Notice tone="warn">{error}</Notice> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('alert.openTitle')}
          value={String(open?.length ?? 0)}
          absent={t('app.notYet')}
          tone={(open?.length ?? 0) > 0 ? 'warn' : 'ok'}
          footerLabel={t('status.critical')}
          footerValue={String(criticalOpen)}
        />
        <KpiCard
          label={t('alert.unacknowledged')}
          value={String(unacknowledged)}
          absent={t('app.notYet')}
          /* The number that actually measures response: an alert nobody has
             picked up is one nobody has decided about. */
          tone={unacknowledged > 0 ? 'crit' : 'ok'}
          footerLabel={t('alert.escalatedCount')}
          footerValue={String(escalated)}
        />
        <KpiCard
          label={t('alert.history')}
          value={String(resolved?.length ?? 0)}
          absent={t('app.notYet')}
          tone="ok"
          footerLabel={t('alert.byEngine')}
          footerValue={String(
            (resolved ?? []).filter((alert) => !alert.resolved_by_name).length,
          )}
        />
        <KpiCard
          label={t('alert.distinctRules')}
          value={String(ruleBars.length)}
          absent={t('app.notYet')}
          tone="neutral"
          footerLabel={t('alert.topRule')}
          footerValue={ruleBars[0]?.label ?? t('app.notYet')}
        />
      </div>

      <Panel>
        <PanelHeader title={t('alert.byRule')} aside={t('alert.byRuleHint')} />
        {/* Rows, for the same reason the remediation screen uses them: the
            category IS a sentence, and a truncated rule name is not a rule. */}
        <div className="space-y-2 px-4 py-4">
          {ruleBars.length === 0 ? (
            <p className="py-4 text-center text-label text-ink-500">{t('app.notYet')}</p>
          ) : (
            ruleBars.map((row) => (
              <BarRow
                key={row.key}
                label={row.label}
                value={row.value}
                max={ruleBars[0]?.value ?? 1}
                display={String(row.value)}
                /* Gold marks a rule that still has an OPEN alert — the same
                   emphasis the column chart carried, kept rather than dropped
                   in the switch to rows. It is «this one is not history yet». */
                className={row.highlight ? 'text-gold' : 'text-brand'}
              />
            ))
          )}
        </div>
      </Panel>

      <Panel>
        <PanelHeader title={t('alert.openTitle')} aside={open ? String(open.length) : undefined} />
        <div className="p-4">
          {open === null ? (
            <Skeleton className="h-24" />
          ) : open.length === 0 ? (
            <EmptyState message={t('alert.empty')} />
          ) : (
            <ul className="divide-y divide-hairline">
              {open.map((alert) => (
                <li key={alert.id}>
                  <button
                    type="button"
                    onClick={() => setParams({ id: alert.id })}
                    className="flex w-full items-start justify-between gap-4 py-3 text-left transition-colors duration-state hover:text-ink-100"
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-label">
                        <span
                          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                            alert.severity === 'critical' ? 'bg-crit' : 'bg-warn'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="font-medium tabular-nums text-ink-100">
                          {alert.short_code}
                        </span>
                        <span className="truncate text-ink-300">{ruleLabel(alert.rule)}</span>
                      </span>
                      <span className="mt-0.5 block pl-3.5 text-micro tabular-nums text-ink-500">
                        {t('alert.since', { at: ageLabel(new Date(alert.opened_at), now, t) })}
                        {alert.acknowledged_by_name
                          ? ` · ${t('alert.acknowledged', { name: alert.acknowledged_by_name })}`
                          : ''}
                      </span>
                    </span>
                    <IconChevron className="h-4 w-4 shrink-0 text-ink-500" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title={t('alert.history')}
          aside={resolved ? String(resolved.length) : undefined}
        />
        <div className="p-4">
          {resolved === null ? (
            <Skeleton className="h-16" />
          ) : resolved.length === 0 ? (
            <EmptyState message={t('alert.historyEmpty')} />
          ) : (
            <ul className="divide-y divide-hairline">
              {resolved.map((alert) => (
                <li key={alert.id} className="py-2.5">
                  <p className="flex items-center gap-2 text-label">
                    <span className="font-medium tabular-nums text-ink-300">
                      {alert.short_code}
                    </span>
                    <span className="truncate text-ink-500">{ruleLabel(alert.rule)}</span>
                  </p>
                  <p className="mt-0.5 text-micro tabular-nums text-ink-500">
                    {/*
                      §4.4 / migration 0026's whole point: NULL `resolved_by`
                      means the condition cleared on its own, a name means a
                      person acted. "It stopped" and "somebody fixed it" are
                      different facts about a kiosk, and an auto-remediation log
                      that cannot tell them apart takes credit for weather.
                    */}
                    {alert.resolved_by_name
                      ? t('alert.resolvedByPerson', { name: alert.resolved_by_name })
                      : t('alert.resolvedByEngine')}
                    {alert.resolution_note ? ` · ${alert.resolution_note}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
    </div>
  );
}
